import { useState } from 'react'

import { Icon } from '@/shared/components'
import { CustomGridToolbar } from '@/shared/components/GridTable/CustomGridToolbar'
import { GridTable } from '@/shared/components/GridTable/GridTable'
import { pxToRem } from '@/shared/styles/themes'
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons'
import { Box, Divider, Paper, styled, Tab, Tabs, Typography } from '@mui/material'
import { GridColDef } from '@mui/x-data-grid-premium'

import {
  AddressValue, DepotFormValues, DriverFormValues, GeneralPreferencesFormValues, GroupFormValues, RunFormValues,
} from '../models/OnboardingFormFields'

type OnboardingSummaryProps = {
  generalPreferences: GeneralPreferencesFormValues
  groupData: GroupFormValues
  depotData: DepotFormValues
  runData: RunFormValues
  driverData: DriverFormValues
  skippedSteps: Set<number>
  isSubmitting: boolean
}

export const OnboardingSummary: React.FC<OnboardingSummaryProps> = ({
  groupData,
  generalPreferences,
  depotData,
  runData,
  driverData,
  skippedSteps,
}) => {
  const [detailsTabValue, setDetailsTabValue] = useState(0)
  const [selectedDetailType, setSelectedDetailType] = useState<'depots' | 'runs' | 'drivers'>('depots')

  const depotColumns: GridColDef[] = [
    {
      field: 'externalId',
      headerName: 'Code',
      width: 120,
    },
    {
      field: 'name',
      headerName: 'Name',
      width: 180,
    },
    {
      field: 'address',
      headerName: 'Address',
      width: 250,
      valueGetter: (address: AddressValue) => {
        return address ? `${address.street}, ${address.suburb}, ${address.state}` : ''
      },
    },
    {
      field: 'uiColor',
      headerName: 'Color',
      width: 100,
      renderCell: (params) => (
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: pxToRem(8),
        }}>
          <div
            style={{
              width: pxToRem(14),
              height: pxToRem(14),
              borderRadius: pxToRem(2),
              backgroundColor: params.row.uiColor?.value || '#ccc',
            }}
          />
          <span>{params.row.uiColor?.label || ''}</span>
        </div>
      ),
    },
  ]

  const runColumns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      width: 180,
    },
    {
      field: 'description',
      headerName: 'Description',
      width: 250,
    },
    {
      field: 'weightCapacity',
      headerName: `Weight Cap. (${generalPreferences.weightUnit})`,
      width: 110,
    },
    {
      field: 'volumeCapacity',
      headerName: `Volume Cap. (${generalPreferences.volumeUnit})`,
      width: 110,
    },
    {
      field: 'uiColorId',
      headerName: 'Color',
      width: 100,
      renderCell: (params) => (
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: pxToRem(8),
        }}>
          <div
            style={{
              width: pxToRem(14),
              height: pxToRem(14),
              borderRadius: pxToRem(2),
              backgroundColor: params.row.uiColorId?.value || '#ccc',
            }}
          />
          <span>{params.row.uiColorId?.label || ''}</span>
        </div>
      ),
    },
  ]

  const driverColumns: GridColDef[] = [
    {
      field: 'login',
      headerName: 'Login',
      width: 120,
    },
    {
      field: 'name',
      headerName: 'Name',
      width: 180,
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 200,
    },
    {
      field: 'mobile',
      headerName: 'Mobile',
      width: 130,
    },
  ]

  // Select the appropriate columns based on tab
  const getColumnsForTab = () => {
    switch (selectedDetailType) {
      case 'depots':
        return depotColumns
      case 'runs':
        return runColumns
      case 'drivers':
        return driverColumns
      default:
        return depotColumns
    }
  }

  // Format the data for the selected tab
  const getDataForTab = () => {
    switch (selectedDetailType) {
      case 'depots':
        return skippedSteps.has(2) ? [] : [{ ...depotData, isSelectable: true }]
      case 'runs':
        return skippedSteps.has(3) ? [] : [{ ...runData, isSelectable: true }]
      case 'drivers':
        return skippedSteps.has(4) ? [] : [{ ...driverData, isSelectable: true }]
      default:
        return []
    }
  }

  return (
    <StyledContainer>
      <StyledPaper>
        <Typography variant="h2" sx={{ fontSize: pxToRem(24), fontWeight: 500, mb: 2 }}>
          Setup Summary
        </Typography>

        <Typography variant="body1" color="text.secondary" paragraph>
          Here's a summary of your {groupData.name} configuration. Please review the details before finalizing your setup.
        </Typography>

        {(skippedSteps.has(2) || skippedSteps.has(3) || skippedSteps.has(4)) && (
          <StyledWarningBox>
            <Icon icon={faInfoCircle} sx={{ color: theme => theme.palette.warning.main, mr: 1 }} />
            <Typography variant="body2">
              {skippedSteps.has(2) ? 'Skipped Depot setup. ' : ''}
              {skippedSteps.has(3) ? 'Skipped Run setup. ' : ''}
              {skippedSteps.has(4) ? 'Skipped Driver setup. ' : ''}
              <br />
              These steps can be completed later under the Admin section.
            </Typography>
          </StyledWarningBox>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Group Information
          </Typography>
          <StyledInfoRow>
            <StyledInfoLabel>Group Name:</StyledInfoLabel>
            <StyledInfoValue>{groupData.name}</StyledInfoValue>
          </StyledInfoRow>
          <StyledInfoRow>
            <StyledInfoLabel>Time Zone:</StyledInfoLabel>
            <StyledInfoValue>{groupData.timeZone?.label || 'Not set'}</StyledInfoValue>
          </StyledInfoRow>
          <StyledInfoRow>
            <StyledInfoLabel>Map Center:</StyledInfoLabel>
            <StyledInfoValue>
              {groupData.mapCenter
                ? `Latitude: ${groupData.mapCenter.latitude}, Longitude: ${groupData.mapCenter.longitude}`
                : 'Default location'
              }
            </StyledInfoValue>
          </StyledInfoRow>
        </Box>

        <StyledTabs
          value={detailsTabValue}
          onChange={(_, newValue) => {
            setDetailsTabValue(newValue)

            switch (newValue) {
              case 0: setSelectedDetailType('depots'); break
              case 1: setSelectedDetailType('runs'); break
              case 2: setSelectedDetailType('drivers'); break
            }
          }}
        >
          <StyledTab
            label={`Depots ${skippedSteps.has(2) ? '(Skipped)' : '(1)'}`}
            iconPosition="end"
            disableRipple
          />
          <StyledTab
            label={`Runs ${skippedSteps.has(3) ? '(Skipped)' : '(1)'}`}
            iconPosition="end"
            disableRipple
          />
          <StyledTab
            label={`Drivers ${skippedSteps.has(4) ? '(Skipped)' : '(1)'}`}
            disableRipple
            iconPosition="end"
          />
        </StyledTabs>

        <Divider />

        <Box sx={{ height: pxToRem(320), mt: 2 }}>
          {getDataForTab().length === 0 ? (
            <StyledEmptyContent>
              <Icon icon={faInfoCircle} sx={{ fontSize: pxToRem(48), color: theme => theme.palette.action.disabled, mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                No {selectedDetailType} configured
              </Typography>
              <Typography variant="body2" color="text.secondary">
                You skipped this step during onboarding. You can add {selectedDetailType} in the Admin section after completing the setup.
              </Typography>
            </StyledEmptyContent>
          ) : (
            <StyledGridTable
              rows={getDataForTab()}
              columns={getColumnsForTab()}
              slots={{
                toolbar: CustomGridToolbar,
              }}
              slotProps={{
                toolbar: {
                  isColumnsEnabled: true,
                  isFilterEnabled: false,
                  isDensityEnabled: true,
                },
              }}
              checkboxSelection={false}
              sx={{
                '& .MuiDataGrid-row': {
                  cursor: 'pointer',
                },
              }}
            />
          )}
        </Box>
      </StyledPaper>
    </StyledContainer>
  )
}

const StyledContainer = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`

const StyledPaper = styled(Paper)`
  width: 100%;
  height: 100%;
  box-shadow: none;
`

const StyledInfoRow = styled(Box)`
  display: flex;
  margin-bottom: ${pxToRem(8)};
`

const StyledInfoLabel = styled(Typography)`
  font-weight: 500;
  width: ${pxToRem(120)};
  flex-shrink: 0;
`

const StyledInfoValue = styled(Typography)`
  flex: 1;
`

const StyledWarningBox = styled(Box)`
  display: flex;
  align-items: flex-start;
  background-color: ${props => props.theme.palette.warning.light}20;
  border-left: 4px solid ${props => props.theme.palette.warning.main};
  padding: ${pxToRem(12)};
  border-radius: ${pxToRem(4)};
  margin-bottom: ${pxToRem(16)};
`

const StyledGridTable = styled(GridTable)`
  height: 100%;
`

const StyledTabs = styled(Tabs)`
  & .MuiTabs-indicator {
    background-color: ${props => props.theme.palette.primary.main};
  }
`

const StyledTab = styled(Tab)`
  text-transform: none;
  min-width: ${pxToRem(100)};
  font-weight: 500;
  
  &.Mui-selected {
    color: ${props => props.theme.palette.primary.main};
  }
`

const StyledEmptyContent = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: ${pxToRem(24)};
  text-align: center;
`
